// Business mappers and builders
import { normalizedEvent } from "../../../business/mappers/customEvents.ga4";

import sanitize from "../../../utils/sanitize";

/**
 * GA4 Builder of events payload to send in the final datalayer object
 * @param {string} eventName Event name to recognize events vs ecommerce tracks
 * @param {object} originalPayload Payload sent from the app who calls track
 * @return {object} Event object to final dataLayer
 *
 * @example
 * object = {
 *   eventName: '',
 *   parameters: {
 *     p1: '',
 *     p2: '',
 *   }
 * }
 *
 */
export const buildEventsGA4Payload = (eventName, originalPayload) => { // NOSONAR
  const event = {
    event: normalizedEvent(eventName),
    parameters: {},
  };

  switch (eventName) {
  /** GA4 events */
  case "feature":
    event.parameters = {
      action: originalPayload.action,
      name: originalPayload.name,
      section: originalPayload.section,
    };
    break;

  case "permissionRequest": 
    event.parameters = {
      ...(originalPayload.permission
        ? {
          permission: originalPayload.permission, 
        }
        : {}),
      ...(originalPayload.status
        ? {
          status: originalPayload.status,      
        }
        : {}),
    };
    break;
   
  case "milestone": 
    event.parameters = {
      ...(originalPayload.name
        ? {
          milestone_name: originalPayload.name,
        }
        : {}),
      ...(originalPayload.type
        ? {
          milestone_type: originalPayload.type,
        }
        : {}),
      ...(originalPayload.status
        ? {
          status: originalPayload.status,
        }
        : {}),
    };
    break; 
  
  case "earnedMiles": 
    event.parameters = {
      ...(originalPayload.section
        ? {
          section: originalPayload.section,
        }
        : {}),
      ...(originalPayload.name
        ? {
          name: originalPayload.name,
        }
        : {}),
      ...(originalPayload.label
        ? {
          label: originalPayload.label,
        }
        : {}),
      // Mapeo condicional para tipos no string.
      ...(originalPayload.miles !== undefined && originalPayload.miles !== null
        ? {
          miles: originalPayload.miles,
        }
        : {}),
      ...(originalPayload.hasMiles !== undefined
        ? {
          has_miles: originalPayload.hasMiles,
        }
        : {}),
    };
    break;  
      
  case "projectedMiles": 
    event.parameters = {
      ...(originalPayload.section
        ? {
          section: originalPayload.section,
        }
        : {}),
      ...(originalPayload.name
        ? {
          name: originalPayload.name,
        }
        : {}),
      ...(originalPayload.miles
        ? {
          miles: originalPayload.miles, 
        }
        : {}),
      ...(originalPayload.projectionMiles !== undefined && originalPayload.projectionMiles !== null
        ? {
          projection_miles: originalPayload.projectionMiles, 
        }
        : {}),
    };
    break;
  case "search":
    event.parameters = {
      ...(originalPayload.type
        ? {
          type: originalPayload.type,
        }
        : {}),
      ...(originalPayload.origin
        ? {
          origin: originalPayload.origin,
        }
        : {}),
      ...(originalPayload.destination
        ? {
          destination: originalPayload.destination,
        }
        : {}),
      ...(originalPayload.departureDate
        ? {
          departure_date: originalPayload.departureDate,
        }
        : {}),
      ...(originalPayload.returnDate
        ? {
          return_date: originalPayload.returnDate,
        }
        : {}),
      ...(originalPayload.product
        ? {
          product: originalPayload.product,
        }
        : {}),
      ...(originalPayload.travelType
        ? {
          travel_type: originalPayload.travelType,
        }
        : {}),
      ...(originalPayload.segments !== undefined 
        ? {
          segments: originalPayload.segments,
        }
        : {}),  
      ...(originalPayload.field
        ? {
          field: sanitize(originalPayload.field),
        }
        : {}),
      ...(originalPayload.usingMiles
        ? {
          using_miles: originalPayload.usingMiles,
        }
        : {}),
      ...(originalPayload.action
        ? {
          action: originalPayload.action,
        }
        : {}),
      ...(originalPayload.status
        ? {
          status: originalPayload.status,
        }
        : {}),    
    };
    break;
  case "searchResults":
    event.parameters = {
      status: originalPayload.status,
      ...(originalPayload.resultsType
        ? {
          results_type: originalPayload.resultsType,
        }
        : {}),
      total_results: originalPayload.totalResults,
      ...(originalPayload.orderBy
        ? {
          order_by: originalPayload.orderBy,
        }
        : {}),
      ...(originalPayload.firstDeparture
        ? {
          first_departure: originalPayload.firstDeparture,
        }
        : {}),
      ...(originalPayload.lastDeparture
        ? {
          last_departure: originalPayload.lastDeparture,
        }
        : {}),
      ...(originalPayload.hasDiscount
        ? {
          has_discount: originalPayload.hasDiscount,
        }
        : {}),
    };
    break;
  case "filterResults":
    event.parameters = {
      criteria: originalPayload.criteria,
      value: originalPayload.value,
      ...(originalPayload.filterBy
        ? {
          filter_by: originalPayload.filterBy,
        }
        : {}),
      ...(originalPayload.orderBy
        ? {
          order_by: originalPayload.orderBy,
        }
        : {}),
      ...(originalPayload.totalResults !== undefined // Usamos !== undefined por si el resultado es 0
        ? {
          total_results: originalPayload.totalResults,
        }
        : {}),
      ...(originalPayload.category
        ? {
          category: originalPayload.category,
        }
        : {}),
      ...(originalPayload.store
        ? {
          store: originalPayload.store,
        }
        : {}),
      ...(originalPayload.milesRange
        ? {
          miles_range: originalPayload.milesRange,
        }
        : {}),
      ...(originalPayload.productBrand
        ? {
          product_brand: originalPayload.productBrand,
        }
        : {}),
      ...(originalPayload.productColor
        ? {
          product_color: originalPayload.productColor,
        }
        : {}),
      ...(originalPayload.productSize
        ? {
          product_size: originalPayload.productSize,
        }
        : {}),
      ...(originalPayload.productVoltage
        ? {
          product_voltage: originalPayload.productVoltage,
        }
        : {}),
      ...(originalPayload.departureDate
        ? {
          departure_date: originalPayload.departureDate,
        }
        : {}),
      ...(originalPayload.returnDate
        ? {
          return_date: originalPayload.returnDate,
        }
        : {}),
    };
    break;
  case "orderResults":
    event.parameters = {
      criteria: originalPayload.criteria,
    };
    break;
  case "boardingPass":
    event.parameters = {
      status: originalPayload.status,
      passenger_type: originalPayload.passengerType,
      passenger_listing: originalPayload.passengerListing,
      origin_airport: originalPayload.originAirport,
      destination_airport: originalPayload.destinationAirport,
      departure_datetime: originalPayload.departureDateTime,
      return_datetime: originalPayload.returnDateTime,
      flight_number: originalPayload.flightNumber,
      ...(originalPayload.passengerId
        ? {
          passenger_id: originalPayload.passengerId,
        }
        : {}),
    };
    break;
  case "upg":
    event.parameters = {
      order: originalPayload.order,
      origin_airport: originalPayload.originAirport,
      destination_airport: originalPayload.destinationAirport,
      departure_datetime: originalPayload.departureDateTime,
      return_datetime: originalPayload.returnDateTime,
      flight_number: originalPayload.flightNumber,
      status: originalPayload.status,
      action: originalPayload.action,
    };
    break;
  case "favouriteRoute":
    event.parameters = {
      step: originalPayload.step,
      origin_city: originalPayload.originCity,
      origin_country: originalPayload.originCountry,
      destination_city: originalPayload.destinationCity,
      destination_country: originalPayload.destinationCountry,
    };
    break;  
  case "boundDetail":
    event.parameters = {
      origin_city: originalPayload.originCity,
      destination_city: originalPayload.destinationCity,
      direction: originalPayload.direction,
      segments: originalPayload.segments,
      ...(originalPayload.departureDateTime
        ? {
          departure_datetime: originalPayload.departureDateTime, 
        }
        : {}),
      ...(originalPayload.returnDateTime 
        ? {
          arrival_datetime: originalPayload.returnDateTime,
        }
        : {}),
      ...(originalPayload.travelType
        ? {
          travel_type: originalPayload.travelType,
        }
        : {}),
      ...(originalPayload.segmentOperator
        ? {
          segment_operator: originalPayload.segmentOperator,
        }
        : {}),  
    };
    break;  
  case "errorLog":
    event.parameters = {
      name: originalPayload.name,
      message: originalPayload.message,
      ...(originalPayload.trackId
        ? {
          track_id: originalPayload.trackId,
        }
        : {}),
      ...(originalPayload.errorCode
        ? {
          error_code: originalPayload.errorCode,
        }
        : {}),
      ...(originalPayload.redirectTo
        ? {
          redirect_to: originalPayload.redirectTo,
        }
        : {}),
      ...(originalPayload.serviceable
        ? {
          serviceable: originalPayload.serviceable,
        }
        : {}),
      ...(originalPayload.passengerId
        ? {
          passenger_id: originalPayload.passengerId,
        }
        : {}),  
    };
    break;
  case "responseLog":
    event.parameters = {
      name: originalPayload.name,
      message: originalPayload.message,
      ...(originalPayload.trackId
        ? {
          track_id: originalPayload.trackId,
        }
        : {}),
      ...(originalPayload.responseCode
        ? {
          response_code: originalPayload.responseCode,
        }
        : {}),
      ...(originalPayload.redirectTo
        ? {
          redirect_to: originalPayload.redirectTo,
        }
        : {}),
      ...(originalPayload.serviceable
        ? {
          serviceable: originalPayload.serviceable,
        }
        : {}),
      ...(originalPayload.passengerId
        ? {
          passenger_id: originalPayload.passengerId,
        }
        : {}),  
    };
    break;
  case "loginSuccess":
    event.parameters = {
      ...(originalPayload.label
        ? {
          label: originalPayload.label,
        }
        : {}),
    };
    break;  
  case "validationLog":
    event.parameters = {
      field: originalPayload.field,
      message: originalPayload.message,
    };
    break;
  case "funnelStep":
  case "checkoutStep":
    event.parameters = {
      step: originalPayload.step,
      details: originalPayload.details,
      ...(originalPayload.segmentODA
        ? {
          segment_oda: originalPayload.segmentODA,
        }
        : {}),
      ...(originalPayload.passengerId
        ? {
          passenger_id: originalPayload.passengerId,
        }
        : {}),
      ...(originalPayload.flightNumber
        ? {
          flight_number: originalPayload.flightNumber,
        }
        : {}),
      ...(originalPayload.status
        ? {
          status: originalPayload.status,
        }
        : {}),      
    };
    break;
  case "repricing":
    event.parameters = {
      type: originalPayload.type,
    };
    break;
  case "shortcut":
  case "menu":
    event.parameters = {
      category: originalPayload.category,
      element: originalPayload.element,
      ...(originalPayload.type
        ? {
          type: originalPayload.type,
        }
        : {}),
      ...(originalPayload.label
        ? {
          label: originalPayload.label,
        }
        : {}),  
    };
    break;
  case "modal":
    event.parameters = {
      action: originalPayload.action,
      name: originalPayload.name,
      ...(originalPayload.step
        ? {
          step: originalPayload.step,
        }
        : {}),
      ...(originalPayload.section
        ? {
          section: originalPayload.section,
        }
        : {}),
      ...(originalPayload.category
        ? {
          category: originalPayload.category,
        }
        : {}),
    };
    break;
  case "select":
  case "display":
  case "card":
  case "show":
  case "whatsApp":
    event.parameters = {
      action: originalPayload.action,
      label: originalPayload.label,
    };
    break;
  case "startForm":
  case "submitForm":
    event.parameters = {
      ...(originalPayload.passengerType
        ? {
          passenger_type: originalPayload.passengerType,
        }
        : {}),
      ...(originalPayload.passengerListing
        ? {
          passenger_listing: originalPayload.passengerListing,
        }
        : {}),
      ...(originalPayload.passengerId
        ? {
          passenger_id: originalPayload.passengerId,
        }
        : {}),  
      ...(originalPayload.details
        ? {
          details: originalPayload.details,
        }
        : {}),
      ...(originalPayload.autocomplete
        ? {
          autocomplete: originalPayload.autocomplete,
        }
        : {}),
      ...(originalPayload.autocompleteInput
        ? {
          autocomplete_input: originalPayload.autocompleteInput,
        }
        : {}),
      ...(originalPayload.editInput
        ? {
          edit_input: originalPayload.editInput,
        }
        : {}),
      ...(originalPayload.autocompleteInputName
        ? {
          autocomplete_input_name: originalPayload.autocompleteInputName,
        }
        : {}),
      ...(originalPayload.editInputName
        ? {
          edit_input_name: originalPayload.editInputName,
        }
        : {}),  
    };
    break;
  case "digitalService":
    event.parameters = {
      service_type: originalPayload.serviceType,
      action: originalPayload.action,
      label: originalPayload.label,
      ...(originalPayload.threadId
        ? {
          thread_id: originalPayload.threadId,
        }
        : {}),
    };
    break;
  case "deeplink":
  case "tab":
    event.parameters = {
      category: originalPayload.category,
      product: originalPayload.product,
      ...(originalPayload.label
        ? {
          label: originalPayload.label,
        }
        : {}),
    };
    break;
  case "accordion":  
    event.parameters = {
      label: originalPayload.label,
      ...(originalPayload.name
        ? {
          name: originalPayload.name,
        }
        : {}),
      ...(originalPayload.flightNumber
        ? {
          flight_number: originalPayload.flightNumber,
        }
        : {}),
      ...(originalPayload.flightDate
        ? {
          flight_date: originalPayload.flightDate, 
        }
        : {}),
      ...(originalPayload.businessUnit
        ? {
          business_unit: originalPayload.businessUnit,
        }
        : {}),
      ...(originalPayload.category
        ? {
          category: originalPayload.category,
        }
        : {}),
    };
    break;
  case "download":
    event.parameters = {
      label: originalPayload.label,
    };
    break;
  case "feedback":
    event.parameters = {
      ...(originalPayload.label
        ? {
          label: originalPayload.label,
        }
        : {}),
      ...(originalPayload.message
        ? {
          message: originalPayload.message,
        }
        : {}),
      ...(originalPayload.category
        ? {
          category: originalPayload.category,
        }
        : {}),
    };
    break;
  case "orderDetail":
    event.parameters = {
      ...(originalPayload.order
        ? {
          order: originalPayload.order, 
        }
        : {}),
      ...(originalPayload.units !== undefined && originalPayload.units !== null
        ? {
          units: originalPayload.units, 
        }
        : {}),
      ...(originalPayload.paymentType
        ? {
          payment_type: originalPayload.paymentType, 
        }
        : {}),
    };
    break;
  
  case "orderData":
    event.parameters = {
      ...(originalPayload.order
        ? {
          order: originalPayload.order,
        }
        : {}),
      ...(originalPayload.status
        ? {
          status: originalPayload.status,
        }
        : {}),
      ...(originalPayload.details
        ? {
          details: originalPayload.details,
        }
        : {}),
      ...(originalPayload.orderDate
        ? {
          order_date: originalPayload.orderDate,
        }
        : {}),
      ...(originalPayload.flightStatus
        ? {
          flight_status: originalPayload.flightStatus,
        }
        : {}),
      ...(originalPayload.orderCountry
        ? {
          order_country: originalPayload.orderCountry,
        }
        : {}),
      ...(originalPayload.orderOrigin
        ? {
          order_origin: originalPayload.orderOrigin,
        }
        : {}),
      ...(originalPayload.orderChannel
        ? {
          order_channel: originalPayload.orderChannel,
        }
        : {}),
      ...(originalPayload.departureDateTime
        ? {
          departure_datetime: originalPayload.departureDateTime,
        }
        : {}),
      ...(originalPayload.returnDateTime
        ? {
          return_datetime: originalPayload.returnDateTime,
        }
        : {}),
      ...(originalPayload.originAirport
        ? {
          origin_airport: originalPayload.originAirport,
        }
        : {}),
      ...(originalPayload.destinationAirport
        ? {
          destination_airport: originalPayload.destinationAirport,
        }
        : {}),
    };
    break;
  case "promo":
    event.parameters = {
      ...(originalPayload.product
        ? {
          product: originalPayload.product,
        }
        : {}),
      ...(originalPayload.origin
        ? {
          origin: originalPayload.origin,
        }
        : {}),
      ...(originalPayload.destination
        ? {
          destination: originalPayload.destination,
        }
        : {}),
      ...(originalPayload.minPrice
        ? {
          minimum_price: originalPayload.minPrice,
        }
        : {}),
      ...(originalPayload.maxPrice
        ? {
          maximum_price: originalPayload.maxPrice,
        }
        : {}),
      ...(originalPayload.orderBy
        ? {
          order_by: originalPayload.orderBy,
        }
        : {}),
      ...(originalPayload.totalResults !== undefined // Usamos !== undefined por si el resultado es 0
        ? {
          total_results: originalPayload.totalResults,
        }
        : {}),
      ...(originalPayload.status
        ? {
          status: originalPayload.status,
        }
        : {}),
      ...(originalPayload.usingMiles
        ? {
          using_miles: originalPayload.usingMiles,
        }
        : {}),
      ...(originalPayload.category
        ? {
          category: originalPayload.category,
        }
        : {}),
      ...(originalPayload.criteria
        ? {
          criteria: originalPayload.criteria,
        }
        : {}),
      ...(originalPayload.duration
        ? {
          duration: originalPayload.duration,
        }
        : {}),
      ...(originalPayload.stops
        ? {
          stops: originalPayload.stops,
        }
        : {}),
      ...(originalPayload.rating
        ? {
          rating: originalPayload.rating,
        }
        : {}),
      ...(originalPayload.property
        ? {
          property: originalPayload.property,
        }
        : {}),
      ...(originalPayload.facilities
        ? {
          facilities: originalPayload.facilities,
        }
        : {}),
      ...(originalPayload.room
        ? {
          room: originalPayload.room,
        }
        : {}),
    };
    break;
  case "click":
    event.parameters = {
      label: originalPayload.label,
      type: originalPayload.type,
      section: originalPayload.section,
      ...(originalPayload.product
        ? {
          product: originalPayload.product,
        }
        : {}),
      ...(originalPayload.category
        ? {
          category: originalPayload.category,
        }
        : {}),
    };
    break;
  case "retry":
    event.parameters = {
      label: originalPayload.label,
    };
    break;  
  case "seatSelection":
    event.parameters = {
      ...(originalPayload.status
        ? {
          status: originalPayload.status,
        }
        : {}),
    };
    break;
    /** Non GA events */
  case "orderContext":
    event.parameters = {
      ...(originalPayload.orderContext
        ? {
          orderContext: originalPayload.orderContext,
        }
        : {}),
      ...(originalPayload.itinerary
        ? {
          itinerary: originalPayload.itinerary,
        }
        : {}),
    };
    break;
    /** Non GA events */
  default:
    // No event parameters required
  }
  /** GA4 events */
  return event;
};
